import React, { useEffect, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import AiLoadingIcon from '~/Common-Objects/LoadingIcons/aiLoadingIcon'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import WarningHolder from '~/Common-Objects/UI/Cards/WarningHolder'
import axios from '~/data/http/axios'
import 'react-circular-progressbar/dist/styles.css'
import i18n from '~/i18n'
import PostHogFunctions from '~/Classes/posthog'

export default function UrlInput({ job, writeAccess, setJob = () => {}, next = () => {} }) {
    const timer = useRef(null)
    const [url, setUrl] = useState('')
    const [analyzing, setAnalyzing] = useState(false)

    const [results, setResults] = useState(null)
    const [error, setError] = useState('')
    const [progressPct, setProgressPct] = useState(-1)

    const start = async () => {
        try {
            setResults(null)
            setError('')
            setAnalyzing(true)

            let uri = (url || '').trim().toLowerCase()
            if (uri && uri.indexOf('http') !== 0) uri = 'https://' + uri
            if (!url || uri.indexOf('@') > -1) {
                setError(i18n.t('templates:brandAnalyzer.urlPicker.invalidWebsite'))
                setAnalyzing(false)
                return
            }
            const res = await axios.post('/accountMaster/siteanalyzer', { url: uri })
            setProgressPct(0)
            setJob(res.data)
            PostHogFunctions.capture('brand_analyzer_started')
        } catch (e) {
            setError(i18n.t('templates:brandAnalyzer.urlPicker.invalidWebsite'))
            setAnalyzing(false)
        }
    }

    const manualRun = async () => {
        try {
            const res = await axios.post('/accountMaster/siteanalyzer')
            setProgressPct(100)
            setJob(res.data)
            setResults(res.data.results)
            next()
        } catch (e) {
            console.log(e)
        }
    }

    const loop = async () => {
        const res = await axios.get('/accountMaster/siteanalyzer/' + job.id)
        setJob(res.data)
        setUrl(res.data.url)
        if (res.data.error) {
            setError(i18n.t('templates:brandAnalyzer.urlPicker.errorUnableToRetrieve'))
            setAnalyzing(false)
            setProgressPct(0)
            return
        }

        let newProg = progressPct
        if (progressPct === 0) {
            newProg = 20
        } else if (progressPct === 20) {
            newProg = 40
        } else if (progressPct === 40) {
            newProg = 60
        } else if (progressPct === 60) {
            newProg = 80
        } else if (progressPct === 80) {
            newProg = 100
        }

        setProgressPct(newProg)

        if (res.data.status === 'complete' && progressPct === 100) {
            setProgressPct(100)
            setAnalyzing(false)
            setResults(res.data.results)
            next()
        }
    }

    useEffect(() => {
        if (analyzing && job.id) {
            timer.current = setTimeout(() => {
                loop()
            }, 1000)
        }

        return function () {
            if (timer.current) clearTimeout(timer.current)
        }
    }, [job])

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ maxWidth: '720px', width: '100%' }}>
                    <h5 className='mar-b10 text-center'>{i18n.t('templates:brandAnalyzer.urlPicker.headerText')}</h5>
                    <p className='mar-b25 text-center'>{i18n.t('templates:brandAnalyzer.urlPicker.subHeaderText')}</p>
                    {!!writeAccess && (
                        <>
                            <div className='quickFlex mar-b25' style={{ alignItems: 'flex-start' }}>
                                <TextInput
                                    outerStyle={{ flexGrow: 1 }}
                                    focus
                                    value={url}
                                    error={error}
                                    onChange={(e) => setUrl(e.currentTarget.value)}
                                    onEnterPress={start}
                                    disabled={!!analyzing || !!results}
                                    placeholder='yourwebsite.com'
                                />
                                <Button
                                    onClick={start}
                                    disabled={analyzing || !!results}
                                    className='button-primary mar-l15'
                                    iconRight={<Icons icon={analyzing ? 'loading' : 'arrowRight'} />}
                                    postHogTag='brand-analyzer-analyze-website'
                                >
                                    {i18n.t('templates:brandAnalyzer.urlPicker.analyzeWebsite')}
                                </Button>
                            </div>
                            <div className='text-grey'>
                                <Trans i18nKey='templates:brandAnalyzer.urlPicker.noWebsite'>
                                    If you don't have a website yet you can manually create your brand
                                    <a onClick={manualRun}>here</a>
                                </Trans>
                            </div>
                        </>
                    )}
                    {!writeAccess && (
                        <WarningHolder>
                            {i18n.t('templates:brandAnalyzer.urlPicker.noAccess')}
                        </WarningHolder>
                    )}

                    {analyzing && (
                        <>
                            <div
                                style={{
                                    marginTop: '50px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexDirection: 'column'
                                }}
                            >
                                <AiLoadingIcon style={{ height: '200px', width: '400px' }} />

                                <div className='mar-t25 text-center'>
                                    <div className='text-heavy'>
                                        {i18n.t('templates:brandAnalyzer.urlPicker.analyzingWebsite')}
                                    </div>
                                    <div>{progressPct === 0 && i18n.t('templates:brandAnalyzer.urlPicker.step1')}</div>
                                    <div>{progressPct === 20 && i18n.t('templates:brandAnalyzer.urlPicker.step2')}</div>
                                    <div>{progressPct === 40 && i18n.t('templates:brandAnalyzer.urlPicker.step3')}</div>
                                    <div>{progressPct === 60 && i18n.t('templates:brandAnalyzer.urlPicker.step4')}</div>
                                    <div>{progressPct === 80 && i18n.t('templates:brandAnalyzer.urlPicker.step5')}</div>
                                    <div>
                                        {progressPct === 100 && i18n.t('templates:brandAnalyzer.urlPicker.step6')}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    )
}
